import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, MenuItem, Button, IconButton,
    Tooltip, CircularProgress, Autocomplete
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { autocompletePopperSlotProps } from "../../utils/autocompletePopperSlotProps";
import ConditionValueEditor from "../shared/ConditionValueEditor";
import {
    applyFieldChange,
    applyOperatorChange,
    catalogFieldId,
    getOperatorLabel,
    operatorsForSelect,
    resolveFieldDescriptor,
    valueKindLabel,
} from "../../utils/conditionFieldUtils";
import { fetchTransactionTypeDetail, mergeConditionCatalog } from "../../hooks/useRuleConditionFields";
import { normalizeBirthdateConditionValue } from "../../utils/birthdateWire";

const COLOR = SECTION_COLORS.rules;

// ─── Serialization ────────────────────────────────────────────────────────────

function serializeNode(node) {
    if (node.type === "group") {
        const children = (node.conditions || []).map(serializeNode).filter(Boolean);
        if (children.length === 0) return null;

        const gType = node.groupType || "and";
        if (gType === "not") {
            if (children.length === 1) return { not: children[0] };
            return { not: { and: children } };
        }
        // Always keep the group wrapper so its operator (or/and) is preserved,
        // even when there is only one child.
        return { [gType]: children };
    } else {
        if (!node.field) return null;
        return {
            field: node.field,
            operator: node.operator || "eq",
            value: normalizeBirthdateConditionValue(node.field, node.value),
        };
    }
}

export function buildConditionsJson(combinator, rows) {
    if (!rows || rows.length === 0) return null;
    const children = rows.map(serializeNode).filter(Boolean);
    if (children.length === 0) return null;
    // Always wrap in the envelope { [combinator]: [...] }.
    // parseConditionsJson always peels this envelope off — no ambiguity.
    return { [combinator || "and"]: children };
}

function parseNode(node) {
    if (!node || typeof node !== "object") return null;

    if ("and" in node && Array.isArray(node.and)) {
        return {
            id: `g_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "group",
            groupType: "and",
            conditions: node.and.map(parseNode).filter(Boolean)
        };
    }
    
    if ("or" in node && Array.isArray(node.or)) {
         return {
            id: `g_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "group",
            groupType: "or",
            conditions: node.or.map(parseNode).filter(Boolean)
        };
    }

    if ("not" in node) {
        const innerNode = node.not;
        let conditions = [];
        if (innerNode && typeof innerNode === "object") {
            if ("and" in innerNode && Array.isArray(innerNode.and)) {
                conditions = innerNode.and.map(parseNode).filter(Boolean);
            } else if ("or" in innerNode && Array.isArray(innerNode.or)) {
                conditions = innerNode.or.map(parseNode).filter(Boolean);
            } else {
                const parsed = parseNode(innerNode);
                if (parsed) conditions = [parsed];
            }
        }
        return {
            id: `g_not_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "group",
            groupType: "not",
            conditions
        };
    }

    if ("field" in node) {
        return {
            id: `c_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "condition",
            field: node.field || "",
            operator: node.operator || node.op || "eq",
            value: normalizeBirthdateConditionValue(node.field, node.value ?? ""),
        };
    }

    return null;
}

// parseConditionsJson: the inverse of buildConditionsJson.
// The top-level { and/or: [...] } is ALWAYS the envelope — its children become root rows.
// Each child is either a condition node or a group node.
export function parseConditionsJson(conditions) {
    const defaultResult = { combinator: "and", rows: [] };
    if (!conditions || typeof conditions !== "object") return defaultResult;

    for (const comb of ["and", "or"]) {
        if (comb in conditions && Array.isArray(conditions[comb])) {
            return {
                combinator: comb,
                rows: conditions[comb].map(parseNode).filter(Boolean),
            };
        }
    }

    // Edge-case: bare { not: ... } or single { field: ... } at root
    if ("not" in conditions || "field" in conditions) {
        const node = parseNode(conditions);
        return { combinator: "and", rows: node ? [node] : [] };
    }

    return defaultResult;
}

// ─── Component Nodes ──────────────────────────────────────────────────────────

const NS_COLORS = { payload: "#15803d", customer: "#0369a1", system: "#7c3aed" };

function ConditionRow({ item, onUpdateItem, onRemoveItem, fieldOptions, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog }) {
    const ns = item.field ? item.field.split(".")[0] : null;
    const nsColor = NS_COLORS[ns] || "#e2e8f0";
    const sx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const allowedOps = operatorsForSelect(item.field, operatorOptions, fieldCatalog, "loyalty", null, item.operator);

    const updateField = (patch) => {
        onUpdateItem({ ...item, ...patch });
    };

    const handleFieldChange = (newField) => {
        onUpdateItem(applyFieldChange(item, newField || "", operatorOptions, fieldCatalog, "loyalty"));
    };

    const handleOperatorChange = (newOperator) => {
        onUpdateItem(applyOperatorChange(item, newOperator, fieldCatalog, "loyalty"));
    };

    return (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e2e8f0", borderLeft: `4px solid ${nsColor}` }}>
            <Box display="flex" gap={1} alignItems="flex-start">
                <Box flex={1} display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Box flex={2} minWidth={180}>
                            <Autocomplete freeSolo={allowCreate} forcePopupIcon size="small" options={fieldOptions}
                                slotProps={autocompletePopperSlotProps}
                                groupBy={(o) => o.startsWith("customer.") ? "Profil client" : o.startsWith("payload.") ? "Payload" : o.startsWith("system.") ? "Système" : "Autres"}
                                getOptionLabel={(o) => !o ? "" : o.includes(".") ? o.substring(o.indexOf(".") + 1) : o}
                                isOptionEqualToValue={(o, v) => o === v}
                                value={item.field || null}
                                loading={loadingFields}
                                onChange={(_, v) => handleFieldChange(v)}
                                onInputChange={(_, v, r) => { if (r === "input") updateField({ field: v || "" }); }}
                                renderInput={(p) => (
                                    <TextField {...p} label="Champ" size="small" sx={sx} placeholder="payload.amount"
                                        InputProps={{ ...p.InputProps, endAdornment: <>{loadingFields && <CircularProgress size={14} />}{p.InputProps.endAdornment}</> }} />
                                )}
                                renderOption={(p, o) => {
                                    const k = o.split(".")[0];
                                    const desc = resolveFieldDescriptor(o, fieldCatalog || {}, "loyalty");
                                    return (
                                        <li {...p} key={o}>
                                            <Box>
                                                <Typography fontSize={13} fontWeight={600} color={NS_COLORS[k] || "#334155"} py={0.25}>
                                                    {o.includes(".") ? o.substring(o.indexOf(".") + 1) : o}
                                                </Typography>
                                                {desc.inputKind && (
                                                    <Typography fontSize={10} color="text.secondary">
                                                        {k === "payload" ? "Payload événement" : k === "customer" ? "Profil client" : "Système"} · {valueKindLabel(desc.inputKind)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    );
                                }}
                            />
                        </Box>
                        <Box flex={1} minWidth={130}>
                            <TextField select size="small" fullWidth label="Opérateur" value={item.operator || "eq"} onChange={(e) => handleOperatorChange(e.target.value)} sx={sx}>
                                {allowedOps.map((op) => <MenuItem key={op} value={op}><Typography fontSize={12}>{getOperatorLabel(op, "loyalty")}</Typography></MenuItem>)}
                            </TextField>
                        </Box>
                    </Box>
                    {item.field && (
                        <ConditionValueEditor
                            dialect="loyalty"
                            field={item.field}
                            operator={item.operator || "eq"}
                            value={item.value}
                            brandName={brandName}
                            catalog={fieldCatalog}
                            accentColor={COLOR.main}
                            onChange={(v) => updateField({ value: v })}
                        />
                    )}
                    {ns && <Typography fontSize={10} color={nsColor} fontWeight={600} px={0.5}>{ns === "payload" ? "📦 Payload" : ns === "customer" ? "👤 Profil client" : "⚙️ Système"}</Typography>}
                </Box>
                <IconButton size="small" onClick={() => onRemoveItem(item.id)} sx={{ color: "#dc2626", mt: 0.5, bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
        </Box>
    );
}

function GroupBox({ item, onUpdateItem, onRemoveItem, level, fieldOptions, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog }) {
    const conds = item.conditions || [];
    const groupType = item.groupType || "and";
    const setGroupType = (gt) => onUpdateItem({ ...item, groupType: gt });

    const GT_OPTIONS = [
        { k: "and", l: "ET", tip: "Toutes les conditions doivent être vraies", bg: COLOR.main },
        { k: "or", l: "OU", tip: "Au moins une condition doit être vraie", bg: "#f59e0b" },
        { k: "not", l: "NON", tip: "Inverse le résultat (Aucune condition ne doit être vraie)", bg: "#dc2626" },
    ];
    const gtMeta = GT_OPTIONS.find((o) => o.k === groupType) || GT_OPTIONS[0];

    const addChildItem = (type) => {
        const newId = (type === "group" ? "g_" : "c_") + Date.now() + "_" + Math.random().toString(36).substr(2,9);
        const newItem = type === "group" 
            ? { id: newId, type: "group", groupType: "and", conditions: [{ id: "c_"+Date.now(), type: "condition", field: "", operator: "eq", value: "" }] }
            : { id: newId, type: "condition", field: "", operator: "eq", value: "" };
        
        onUpdateItem({ ...item, conditions: [...conds, newItem] });
    };

    const handleUpdateChild = (patchedChild) => {
        onUpdateItem({ ...item, conditions: conds.map(c => c.id === patchedChild.id ? patchedChild : c) });
    };

    const handleRemoveChild = (id) => {
        onUpdateItem({ ...item, conditions: conds.filter(c => c.id !== id) });
    };

    const shared = { fieldOptions, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog };
    
    // ── Depth-based background palette ─────────────────────────────────────────
    // Each depth gets a distinct tint so nested groups are instantly recognisable.
    const depthBg = [
        "#eef9ff", // level 0 – light sky
        "#fefce8", // level 1 – light amber
        "#f0fdf4", // level 2 – light mint
        "#fdf4ff", // level 3 – light violet
        "#fff1f2", // level 4 – light rose
    ];
    const bgColor = depthBg[Math.min(level, depthBg.length - 1)];

    // Operator accent color drives the left border and icon tint
    const accentColor = gtMeta.bg;

    return (
        <Box sx={{
            borderRadius: 2,
            border: `1px solid ${accentColor}30`,
            borderLeft: `4px solid ${accentColor}`,
            bgcolor: bgColor,
            p: 2,
            position: "relative",
            boxShadow: `0 1px 4px ${accentColor}18`,
        }}>
            {/* Depth label */}
            {level > 0 && (
                <Typography sx={{
                    position: "absolute", top: 6, right: 10,
                    fontSize: 9, fontWeight: 700, color: `${accentColor}80`,
                    textTransform: "uppercase", letterSpacing: 1,
                }}>
                    Niveau {level + 1}
                </Typography>
            )}

            {/* Group Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <FolderOpenIcon sx={{ fontSize: 18, color: accentColor }} />
                    <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: `1.5px solid ${accentColor}50`, bgcolor: "#fff", boxShadow: `0 1px 3px ${accentColor}20` }}>
                        {GT_OPTIONS.map(({ k, l, bg }) => (
                            <Tooltip key={k} title={GT_OPTIONS.find((o) => o.k === k)?.tip || ""}>
                                <Box onClick={() => setGroupType(k)} sx={{
                                    px: 2, py: 0.5, cursor: "pointer", fontSize: 11, fontWeight: 800,
                                    bgcolor: groupType === k ? bg : "transparent",
                                    color: groupType === k ? "#fff" : "#64748b",
                                    transition: "all .2s", userSelect: "none",
                                    "&:hover": { bgcolor: groupType === k ? bg : "#f8fafc", color: groupType === k ? "#fff" : "#334155" },
                                }}>{l}</Box>
                            </Tooltip>
                        ))}
                    </Box>
                    {groupType === "not" && <Typography fontSize={11} color="#dc2626" fontWeight={600} ml={1}>⚠️ Inversion</Typography>}
                </Box>

                <Box display="flex" gap={1} alignItems="center">
                     {(groupType !== "not" || conds.length === 0) && (
                        <>
                             <Button size="small" variant="outlined" onClick={() => addChildItem("condition")}
                                sx={{ fontSize: 11, textTransform: "none", color: COLOR.main, borderColor: `${COLOR.main}50`, py: 0.2, px: 1, minWidth: 0, bgcolor: "#fff" }}>
                                + Condition
                             </Button>
                             <Button size="small" variant="outlined" onClick={() => addChildItem("group")}
                                sx={{ fontSize: 11, textTransform: "none", color: "#7c3aed", borderColor: "#7c3aed50", py: 0.2, px: 1, minWidth: 0, bgcolor: "#fff" }}>
                                + Sous-groupe
                             </Button>
                        </>
                     )}
                     <IconButton size="small" onClick={() => onRemoveItem(item.id)} sx={{ color: "#dc2626", ml: 1, bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}>
                         <DeleteOutlineIcon fontSize="small" />
                     </IconButton>
                </Box>
            </Box>

            {/* Group Content */}
            <Box sx={{ pl: 1, borderLeft: `2px solid ${accentColor}20` }}>
                {conds.length === 0 ? (
                    <Typography fontSize={12} color="text.secondary" textAlign="center" py={2} fontStyle="italic">
                        Ce groupe est vide. Ajoutez des conditions ou sous-groupes.
                    </Typography>
                ) : (
                    conds.map((child, idx) => (
                        <ConditionItem
                            key={child.id}
                            item={child}
                            isFirst={idx === 0}
                            parentGroupType={groupType}
                            onUpdateItem={handleUpdateChild}
                            onRemoveItem={handleRemoveChild}
                            level={level + 1}
                            {...shared}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
}

const ConditionItem = ({ item, isFirst, onUpdateItem, onRemoveItem, level = 0, ...shared }) => {
    return (
        <React.Fragment>
            {!isFirst && (
                <Box sx={{ height: "1px", bgcolor: "#e2e8f0", my: 1.5 }} />
            )}
            
            {item.type === "group" ? (
                <GroupBox item={item} onUpdateItem={onUpdateItem} onRemoveItem={onRemoveItem} level={level} {...shared} />
            ) : (
                <ConditionRow item={item} onUpdateItem={onUpdateItem} onRemoveItem={onRemoveItem} level={level} {...shared} />
            )}
        </React.Fragment>
    );
};

// ─── Main ConditionBuilder ───────────────────────────────────────────────────

export default function ConditionBuilder({
    brandName, transactionType, conditionCatalog, uiHints,
    combinator, rows, onChange,
    payloadPaths, hasPayloadSchema,
}) {
    const [fieldOpts, setFieldOpts] = useState([]);
    const [fieldCatalog, setFieldCatalog] = useState(null);
    const [loadingFields, setLoadingFields] = useState(false);

    const fieldDatasource = uiHints?.field?.datasource || {
        endpoint: "/admin/rules/ui-options/condition-fields",
        brandVia: "X-Brand",
        valueField: "items",
        dependsOn: { transaction_type: "transaction_type" },
    };
    const allowCreate = uiHints?.field?.allowCreate !== false;
    const operatorOptions = uiHints?.operator?.options || ["eq","neq","gt","gte","lt","lte","between","in","contains","exists"];

    useEffect(() => {
        if (!brandName || !transactionType) { setFieldOpts([]); setFieldCatalog(null); return; }
        const url = new URL(`${LOYALTY_API_BASE}${fieldDatasource.endpoint}`);
        if (fieldDatasource.dependsOn?.transaction_type) url.searchParams.set("transaction_type", transactionType);
        const headers = fieldDatasource.brandVia ? { [fieldDatasource.brandVia]: brandName } : {};
        setLoadingFields(true);
        Promise.all([
            loyaltyFetch(url.toString(), { headers }).then((r) => (r.ok ? r.json() : null)),
            fetchTransactionTypeDetail(brandName, transactionType),
        ])
            .then(([res, txDetail]) => {
                const enriched = mergeConditionCatalog(res, txDetail);
                const items = enriched?.[fieldDatasource.valueField || "items"];
                const ids = Array.isArray(items) ? items.map(catalogFieldId).filter(Boolean) : [];
                setFieldOpts(ids);
                setFieldCatalog(enriched && typeof enriched === "object" ? enriched : null);
            })
            .catch(() => { setFieldOpts([]); setFieldCatalog(null); })
            .finally(() => setLoadingFields(false));
    }, [brandName, transactionType]);

    const sortedFieldOpts = React.useMemo(() =>
        [...fieldOpts].sort((a, b) => {
            const g = (o) => o.startsWith("customer.") ? 1 : o.startsWith("payload.") ? 2 : o.startsWith("system.") ? 3 : 4;
            return g(a) !== g(b) ? g(a) - g(b) : a.localeCompare(b);
        }), [fieldOpts]);

    const [topCombinator, setTopCombinator] = useState(combinator || "and");
    useEffect(() => { setTopCombinator(combinator || "and"); }, [combinator]);

    const emit = (newRows, tc) => {
        onChange(tc ?? topCombinator, newRows);
    };

    const addRootItem = (type) => {
         const newId = (type === "group" ? "g_" : "c_") + Date.now() + "_" + Math.random().toString(36).substr(2,9);
         const newItem = type === "group" 
            ? { id: newId, type: "group", groupType: "and", conditions: [{ id: "c_"+Date.now(), type: "condition", field: "", operator: "eq", value: "" }] }
            : { id: newId, type: "condition", field: "", operator: "eq", value: "" };
         emit([...rows, newItem]);
    };
    
    const handleUpdateRootItem = (patchedItem) => {
         emit(rows.map(r => r.id === patchedItem.id ? patchedItem : r));
    }
    
    const handleRemoveRootItem = (id) => {
         emit(rows.filter(r => r.id !== id));
    }

    const changeTopCombinator = (tc) => {
        setTopCombinator(tc);
        emit(rows, tc);
    };

    const shared = { fieldOptions: sortedFieldOpts, operatorOptions, loadingFields, allowCreate, brandName, fieldCatalog };

    return (
        <Box sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${COLOR.main}40`, bgcolor: "#f8fafc" }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                 <Box display="flex" alignItems="center" gap={1}>
                     <FilterAltIcon sx={{ fontSize: 22, color: COLOR.main }} />
                     <Typography fontSize={15} fontWeight={800} color="#0f172a">Conditions d'application</Typography>
                     {loadingFields && <CircularProgress size={14} sx={{ color: COLOR.main, ml: 1 }} />}
                 </Box>
                 
                 <Box display="flex" alignItems="center" gap={2}>
                     {/* Global combinator — shown only when there are ≥2 root items */}
                     {rows.length > 1 && (
                         <Box display="flex" alignItems="center" gap={1}>
                             <Typography fontSize={12} color="text.secondary" fontWeight={600}>Lier par :</Typography>
                             <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: "1px solid #cbd5e1", bgcolor: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
                                 {[{ k: "and", l: "ET", bg: COLOR.main }, { k: "or", l: "OU", bg: "#f59e0b" }].map(({ k, l, bg }) => (
                                     <Box key={k} onClick={() => changeTopCombinator(k)} sx={{
                                         px: 2.5, py: 0.5, cursor: "pointer", fontSize: 12, fontWeight: 800,
                                         bgcolor: topCombinator === k ? bg : "transparent",
                                         color: topCombinator === k ? "#fff" : "#64748b",
                                         transition: "all .2s", userSelect: "none",
                                         "&:hover": { bgcolor: topCombinator === k ? bg : "#f8fafc", color: topCombinator === k ? "#fff" : "#334155" },
                                     }}>{l}</Box>
                                 ))}
                             </Box>
                         </Box>
                     )}
                     <Box display="flex" gap={1}>
                     <Button size="small" variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => addRootItem("condition")}
                         disabled={!transactionType}
                         sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 13, px: 2, bgcolor: COLOR.main, color: "#fff", "&:hover": { bgcolor: "#0284c7" } }}>
                         Condition
                     </Button>
                     <Button size="small" variant="outlined" startIcon={<FolderOpenIcon />} onClick={() => addRootItem("group")}
                         disabled={!transactionType}
                         sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 13, px: 2, color: "#7c3aed", borderColor: "#7c3aed", bgcolor: "#fff", "&:hover": { bgcolor: "#f3e8ff", borderColor: "#7c3aed" } }}>
                         Groupe
                     </Button>
                     </Box>
                 </Box>
            </Box>
            
            {/* Content */}
            <Box sx={{ minHeight: rows.length === 0 ? 100 : "auto", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                {!transactionType && <Typography fontSize={14} color="text.secondary" textAlign="center" py={3}>Sélectionnez un type de transaction pour configurer les conditions.</Typography>}
                {transactionType && rows.length === 0 && <Typography fontSize={14} color="text.secondary" textAlign="center" py={3} fontStyle="italic">Aucune condition — la règle s'appliquera à toutes les transactions.</Typography>}
                
                {rows.map((item, idx) => (
                     <ConditionItem 
                         key={item.id} 
                         item={item} 
                         isFirst={idx === 0} 
                         parentGroupType="and"
                         onUpdateItem={handleUpdateRootItem} 
                         onRemoveItem={handleRemoveRootItem}
                         level={0}
                         {...shared}
                     />
                ))}
            </Box>
        </Box>
    );
}
